import { all, call, put, takeLatest } from "redux-saga/effects";
import pako from 'pako';
import {
  apiGetCall,
  apiPatchCall,
  apiPutCall,
} from "../api";
import {
  setAllStatuses,
  getAllStatuses,
  setAllGdsAggregator,
  getAllGdsAggregator,
  getCSVData,
  getSearchedSubSellers,
  setSelectedSubSeller,
  setSearchedSubSellers,
  getSubSellerProfile,
  setSubSellerProfile,
  getAllProfileStatuses,
  setAllProfileStatuses,
  getProfileDetail,
  setProfileDetail,
  approveSubseller,
  getAllRejectedReason,
  setAllRejectedReasonSaga
} from "../slices/subSellerSlice";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { resetAppLoading, setAppLoading, setNotification } from "../slices/notificationSlice";


const URL = process.env.REACT_APP_API_URL;

function* checkTokenExpired() {
  yield window.instance
    .acquireTokenSilent({
      redirectUri: process.env.REACT_APP_AZURE_REDIRECT,
      authority: process.env.REACT_APP_AZURE_AUTHORITY,
    })
    .then((res) => {
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.idToken}`;
    })
    .catch((e) => {
      if (e instanceof InteractionRequiredAuthError) {
        window.instance.loginRedirect();
      }
    });
}

function s2ab(s) {
  var buf = new ArrayBuffer(s.length);
  var view = new Uint8Array(buf);
  for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
}

function* getCSVDataSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    const response = yield call(axios.get, `${URL}${"admin/download-sub-seller"}?status=${action.payload.status}&seller_id=${action.payload.seller_id}`, {
      responseType: 'arraybuffer'
    });
    
    const uint8Array = new Uint8Array(response.data);
    
    const isGzip = uint8Array[0] === 0x1f && uint8Array[1] === 0x8b;
    
    let finalData;
    
    if (isGzip) {
      const decompressed = pako.inflate(uint8Array);
      
      const base64String = new TextDecoder().decode(decompressed);
      finalData = s2ab(atob(base64String));
    } else {
      const base64String = new TextDecoder().decode(uint8Array);
      finalData = s2ab(atob(base64String));
    }
    
    const blob = new Blob([finalData], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "subSellers.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    yield put(
      setNotification({
        message: "File downloaded successfully",
        severity: "success",
        open: true,
      })
    );
  } catch (error) {
    console.error('Download error:', error);
    yield put(
      setNotification({
        message: "Export failed. Please try again later.",
        severity: "error",
        open: true,
      })
    );
  } finally {
    console.log("finally");
    yield put(resetAppLoading())
  }
}

function* getAllStatusesSaga() {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + "admin/sub-seller-status");
    yield put(setAllStatuses(data));
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch sub-seller statuses.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getAllProfileStatusesSaga() {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + "admin/sub-seller-status?type=profile");
    yield put(setAllProfileStatuses(data));
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch profile statuses.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getProfileDetailSaga(action) {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + `admin/sub-seller-profile/${action.payload.sellerId}`);
    yield put(setProfileDetail(data));
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch sub-seller profile details.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getAllGdsAggregatorSaga() {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + "admin/aggregator-filter");
    yield put(setAllGdsAggregator(data));
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch aggregators.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getAllRejectedReasonSaga() {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + "admin/sub-seller-rejection");
    yield put(setAllRejectedReasonSaga(data));
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch rejection reasons.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getSearchedSubSellerSaga(action) {
  try {
    yield call(checkTokenExpired);

    yield put(
      setSearchedSubSellers({
        isInput: action.payload.isInput,
        loading: true,
        change: null,
        ...(action.payload.params?.searchTerm && {
          searchTerm: action.payload.params.searchTerm,
        }),
      })
    );
    const data = yield call(
      apiGetCall,
      `${URL}${"admin/sub-seller?"}${new URLSearchParams(
        action.payload.params
      ).toString()}`
    );
    // yield delay(5000);
    yield put(
      setSearchedSubSellers({
        ...data,
        isInput: action.payload.isInput,
        loading: false,
        change: action.payload.params.status  || action.payload.isInput,
      })
    );
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Unable to fetch sub-seller list.",
        severity: "error",
        open: true,
      })
    );
  }
}
  

function* setSelectedSubSellerSaga(action) {
  try {
    const data = yield call(
      apiPatchCall,
      URL + "sellers/" + action.payload.id,
      { ...action.payload }
    );
    if (data) {
      yield put(
        setNotification({
          message: "Sub-seller updated successfully.",
          severity: "success",
          open: true,
        })
      );
    }
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Something went wrong. Try again!",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getSubSellerProfileSaga(action) {
    try {
      yield call(checkTokenExpired);
      const data = yield call(
        apiPatchCall,
        URL + "admin/sub-seller/" + action.payload.id,
        { ...action.payload }
      );
      if (data) {
        yield put(setSubSellerProfile(data));
        yield put(
          setNotification({
            message: "Sub-seller profile updated successfully.",
            severity: "success",
            open: true,
          })
        );
      }
    } catch (error) {
      console.log(error);
      yield put(
        setNotification({
          message: "Something went wrong. Try again!",
          severity: "error",
          open: true,
        })
      );
    }
  }

  function* approveSubsellerSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading())

    const data = yield call(
      apiPutCall,
      URL + `admin/sub-sellers-status/${action.payload.sellerId}`,
      action.payload.data
    );
    if (data.status === "Successful") {
      yield put(
        setNotification({
          message: action.payload.data.sub_seller_status_id === "SSS003" ? "Sub-Seller rejected" : "Sub-Seller approved",
          severity: "success",
          open: true,
        }));
        const data = yield call(apiGetCall, URL + `admin/sub-seller-profile/${action.payload.sellerId}`);
        yield put(setProfileDetail(data));
    }
  } catch (error) {
    console.log(error);
    yield put(
      setNotification({
        message: "Something went wrong. Try again!",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(resetAppLoading())

  }
}


function* sagas() {
  yield all([
    takeLatest(getAllStatuses.type, getAllStatusesSaga),
    takeLatest(approveSubseller.type, approveSubsellerSaga),
    takeLatest(getAllProfileStatuses.type, getAllProfileStatusesSaga),   
    takeLatest(getAllRejectedReason.type, getAllRejectedReasonSaga),       
    takeLatest(getProfileDetail.type, getProfileDetailSaga),        
    takeLatest(getAllGdsAggregator.type, getAllGdsAggregatorSaga),
    takeLatest(getCSVData.type, getCSVDataSaga),
    takeLatest(getSearchedSubSellers.type, getSearchedSubSellerSaga),
    takeLatest(setSelectedSubSeller.type, setSelectedSubSellerSaga),
    takeLatest(getSubSellerProfile.type, getSubSellerProfileSaga),
  ]);
}
export default sagas;
